// ============================================================================
// Écriture à DÉVELOPPER ou à FACTORISER (moteur v33, refonte lot 6.3).
// ============================================================================
// Une feuille du noyau d'objectif `simplify` avec la clé `form` : « A = (2x − 3)(3x + 4) »
// à développer et réduire, « A = x² − 25 » à factoriser. Le noyau prouve que chaque
// ligne garde la même valeur ; ce module mesure seulement la FORME de l'écriture, qui
// dit si une ligne avance :
//   - développer : produits (et parenthèses) encore à enlever, puis calculs ;
//   - factoriser : une somme (10), une somme qui montre un facteur commun, a² − b² ou
//     a² ± 2ab + b² (7), un produit encore à réduire (2), un produit réduit (0).
// Pur, sans état : kernel-sheet.ts s'en sert pour juger et nommer les pas.
// ============================================================================

import { abs, rat } from './kernel/exact';
import * as Q from './kernel/exact';
import { polyDivExact, polyIsConstant, polySymbols, rationalRoots, toRatFn } from './kernel/normal';
import { canon, parseStatement, termKey, type Term } from './kernel/term';
import { isSupPow, lex } from './lexer';
import { univariateCoefs } from './kernel/witness';
import type { KernelForm } from './model';

/** Produits (de sommes) encore à développer. */
export function unexpanded(t: Term): number {
  switch (t.k) {
    case 'mul':
      return (t.c.some((c) => c.k === 'add') ? 1 : 0) + t.c.reduce((s, c) => s + unexpanded(c), 0);
    case 'pow':
      return (t.a.k === 'add' ? 1 : 0) + unexpanded(t.a) + unexpanded(t.b);
    case 'add':
      return t.c.reduce((s, c) => s + unexpanded(c), 0);
    case 'div':
    case 'binom':
      return unexpanded(t.a) + unexpanded(t.b);
    case 'fn':
      return unexpanded(t.a);
    default:
      return 0;
  }
}

/**
 * Multiplications de sommes encore à faire, une par développement (moteur v41) — la forme
 * d'une écriture à développer : « (x − 1)(x + 1)(x − 2) » 2, « (x² − 1)(x − 2) » 1,
 * « 3(x + 1) » 1, « (x + 1)³ » 2. `unexpanded` ne distingue pas deux sommes de trois.
 */
export function expandCount(t: Term): number {
  switch (t.k) {
    case 'mul': {
      let sums = 0;
      let others = 0;
      let inner = 0;
      for (const c of t.c) {
        const e = c.k === 'pow' && c.a.k === 'add' ? intExp(c.b) : null;
        if (c.k === 'add') {
          sums += 1;
          inner += expandCount(c);
        } else if (e !== null && c.k === 'pow') {
          sums += e;
          inner += expandCount(c.a);
        } else {
          others += 1;
          inner += expandCount(c);
        }
      }
      return (sums ? sums - 1 + (others ? 1 : 0) : 0) + inner;
    }
    case 'pow': {
      if (t.a.k === 'add') {
        const e = intExp(t.b);
        return (e !== null ? e - 1 : 1) + expandCount(t.a) + expandCount(t.b);
      }
      return expandCount(t.a) + expandCount(t.b);
    }
    case 'add':
      return t.c.reduce((s, c) => s + expandCount(c), 0);
    case 'div':
    case 'binom':
      return expandCount(t.a) + expandCount(t.b);
    case 'fn':
      return expandCount(t.a);
    default:
      return 0;
  }
}

/** Parenthèses inutiles : une somme écrite entre parenthèses dans une somme (« + (x − 4) »). */
export function nestedSums(t: Term): number {
  switch (t.k) {
    case 'add':
      return t.c.reduce((s, c) => s + (c.k === 'add' ? 1 : 0) + nestedSums(c), 0);
    case 'mul':
      return t.c.reduce((s, c) => s + nestedSums(c), 0);
    case 'pow':
    case 'div':
    case 'binom':
      return nestedSums(t.a) + nestedSums(t.b);
    case 'fn':
      return nestedSums(t.a);
    default:
      return 0;
  }
}

/** facteurs d'un produit, à plat (« 2x × 3x » → 2, x, 3, x) */
function flatMul(t: Term): Term[] {
  return t.k === 'mul' ? t.c.flatMap(flatMul) : [t];
}
const isNum = (t: Term): boolean => t.k === 'num';
const intExp = (t: Term): number | null =>
  t.k === 'num' && t.v.d === 1n && t.v.n >= 1n && t.v.n <= 8n ? Number(t.v.n) : null;

/**
 * la base d'une puissance (aⁿ → a, et (aⁿ)ᵐ → a), sinon le terme lui-même : dans
 * « (x³)² × x⁴ », (x³)² et x⁴ se regroupent ENCORE après « (x³)² → x⁶ » (lot 6.6)
 */
const baseOf = (t: Term): Term => (t.k === 'pow' ? baseOf(t.a) : t);

/**
 * Règles des puissances encore à appliquer : aⁿ × aᵐ, aⁿ ÷ aᵐ, (aⁿ)ᵐ, (2x³)², (−x)⁷.
 */
export function powDebt(t: Term): number {
  switch (t.k) {
    case 'mul': {
      const groups = new Map<string, number>();
      flatMul(t)
        .filter((f) => !isNum(f) && f.k !== 'add')
        .forEach((f) => {
          const k = termKey(canon(baseOf(f)));
          groups.set(k, (groups.get(k) ?? 0) + 1);
        });
      let d = 0;
      groups.forEach((n) => (d += n - 1));
      return d + t.c.reduce((s, c) => s + powDebt(c), 0);
    }
    case 'pow': {
      let d = 0;
      if (t.a.k === 'pow') d += 1;
      const fs = t.a.k === 'mul' ? flatMul(t.a) : [];
      if (fs.length > 1 && fs.some((f) => !isNum(f))) {
        d += 1;
        // moteur v41 : (ab)ⁿ = aⁿbⁿ, puis chaque facteur se calcule — « (2x)³ » 2,
        // « 2³ × x³ » 1 ; « (−3a²)³ » 3 : (−3)³ et (a²)³ restent à calculer
        d += fs.filter(
          (f) =>
            (f.k === 'num' && !(f.v.d === 1n && (f.v.n === 1n || f.v.n === -1n))) || f.k === 'pow',
        ).length;
      }
      return d + powDebt(t.a) + powDebt(t.b);
    }
    case 'div': {
      const keys = (u: Term): Set<string> =>
        new Set(
          flatMul(u)
            .filter((f) => !isNum(f) && f.k !== 'add')
            .map((f) => termKey(canon(baseOf(f)))),
        );
      const ka = keys(t.a);
      const kb = [...keys(t.b)];
      let shared = kb.some((k) => ka.has(k)) ? 1 : 0;
      // moteur v41 : une somme dont chaque terme (une fois les nombres calculés) contient un
      // facteur du dénominateur (« (3h + 3h² + h³) ÷ h ») se simplifie encore
      const num = canon(t.a);
      if (!shared && num.k === 'add' && kb.length) {
        const terms = num.c.map(keys);
        if (kb.some((k) => terms.every((tk) => tk.has(k)))) shared = 1;
      }
      return shared + powDebt(t.a) + powDebt(t.b);
    }
    case 'add':
      return t.c.reduce((s, c) => s + powDebt(c), 0);
    case 'fn':
      return powDebt(t.a);
    default:
      return 0;
  }
}

/**
 * Facteurs écrits d'un terme d'une somme, sans son signe : « −7x × 2 » → 7, x, 2 ;
 * « (x + 1)² » → (x + 1), (x + 1) ; un coefficient négatif compte par sa valeur absolue.
 */
function termFactors(t: Term): Term[] {
  const out: Term[] = [];
  for (const f of flatMul(t)) {
    if (f.k === 'num') {
      // le « − » d'une soustraction n'est pas un facteur écrit
      if (f.v.n === -1n && f.v.d === 1n) continue;
      out.push({ k: 'num', v: abs(f.v) });
      continue;
    }
    const e = f.k === 'pow' ? intExp(f.b) : null;
    if (f.k === 'pow' && e !== null && e <= 4 && f.a.k !== 'num') {
      const inner = f.a.k === 'mul' ? flatMul(f.a) : [f.a];
      for (let i = 0; i < e; i++) out.push(...inner);
      continue;
    }
    out.push(f);
  }
  return out;
}
const factorKey = (f: Term): string => termKey(canon(f));

/** Chaque terme montre le même facteur ET ce qui reste (« 9x + 9 × 1 », « 7x × x − 7x × 2 »). */
function showsCommonFactor(terms: Term[]): boolean {
  const lists = terms.map(termFactors);
  if (lists.some((l) => l.length < 2)) return false;
  const count = (l: Term[]): Map<string, number> => {
    const m = new Map<string, number>();
    l.forEach((f) => m.set(factorKey(f), (m.get(factorKey(f)) ?? 0) + 1));
    return m;
  };
  const counts = lists.map(count);
  const one = termKey({ k: 'num', v: rat(1) });
  // le facteur commun écrit : chaque facteur, autant de fois que dans chaque terme
  const common = new Map<string, number>();
  counts[0]!.forEach((n, k) => {
    const min = Math.min(n, ...counts.slice(1).map((m) => m.get(k) ?? 0));
    if (min > 0 && k !== one) common.set(k, min);
  });
  if (!common.size) return false;
  // … et c'est TOUT le facteur commun : « 12x² − 8x » montre x, pas 4x
  const shown = lists[0]!.filter((f) => f.k === 'num' && common.has(factorKey(f)));
  let shownNum = 1n;
  const seen = new Set<string>();
  shown.forEach((f) => {
    const k = factorKey(f);
    if (seen.has(k) || f.k !== 'num' || f.v.d !== 1n) return;
    seen.add(k);
    for (let i = 0; i < common.get(k)!; i++) shownNum *= f.v.n;
  });
  const info = terms.map(monomialInfo);
  if (info.every((i) => i !== null)) {
    const g = info.reduce((a, i) => gcdBig(a, i!.coef), 0n);
    if (g > 1n && g !== shownNum) return false;
    const syms = new Set(info.flatMap((i) => [...i!.exps.keys()]));
    for (const x of syms) {
      const min = Math.min(...info.map((i) => i!.exps.get(x) ?? 0));
      if (min > 0 && (common.get(termKey({ k: 'sym', n: x })) ?? 0) !== min) return false;
    }
  }
  return true;
}

/**
 * Coefficient entier et exposants d'un terme sans somme (« 12x² » → 12, x : 2) ;
 * null s'il contient une somme ou un coefficient non entier.
 */
function monomialInfo(t: Term): { coef: bigint; exps: Map<string, number> } | null {
  const c = canon(t);
  const fs = c.k === 'mul' ? c.c : [c];
  let coef = 1n;
  const exps = new Map<string, number>();
  for (const f of fs) {
    if (f.k === 'num') {
      if (f.v.d !== 1n) return null;
      coef *= f.v.n < 0n ? -f.v.n : f.v.n;
    } else if (f.k === 'sym') exps.set(f.n, (exps.get(f.n) ?? 0) + 1);
    else if (f.k === 'pow' && f.a.k === 'sym' && intExp(f.b) !== null)
      exps.set(f.a.n, (exps.get(f.a.n) ?? 0) + intExp(f.b)!);
    else return null;
  }
  return { coef, exps };
}

/** une puissance 2 écrite (« 5² », « (2x)² », « (x + 3)² ») ; sa base, sinon null */
function squareBase(t: Term): Term | null {
  return t.k === 'pow' && intExp(t.b) === 2 ? t.a : null;
}
/** le signe d'un terme d'une somme : « −5² » → { neg: true, body: 5² } */
function signed(t: Term): { neg: boolean; body: Term } {
  if (t.k === 'mul' && t.c[0]?.k === 'num' && t.c[0].v.n === -1n && t.c[0].v.d === 1n)
    return { neg: true, body: t.c.length === 2 ? t.c[1]! : { k: 'mul', c: t.c.slice(1) } };
  return { neg: false, body: t };
}

/** « a² − b² » écrit avec ses deux carrés (« x² − 5² », « (2x − 1)² − (x + 3)² ») */
function showsSquares(terms: Term[]): boolean {
  if (terms.length !== 2) return false;
  const [a, b] = terms.map(signed);
  return a!.neg !== b!.neg && !!squareBase(a!.body) && !!squareBase(b!.body);
}

/** « a² ± 2ab + b² » écrit avec ses carrés et son double produit (« x² + 2x × 1 + 1² ») */
function showsPerfectSquare(terms: Term[]): boolean {
  if (terms.length !== 3) return false;
  const sq = terms
    .map((t) => ({ t, s: signed(t) }))
    .filter((u) => !u.s.neg && squareBase(u.s.body));
  if (sq.length < 2) return false;
  for (let i = 0; i < sq.length; i++)
    for (let j = i + 1; j < sq.length; j++) {
      const mid = terms.find((t) => t !== sq[i]!.t && t !== sq[j]!.t);
      if (!mid) continue;
      const want = [
        { k: 'num', v: rat(2) } as Term,
        ...termFactors(squareBase(sq[i]!.s.body)!),
        ...termFactors(squareBase(sq[j]!.s.body)!),
      ]
        .map(factorKey)
        .sort();
      const got = termFactors(signed(mid).body).map(factorKey).sort();
      if (want.length === got.length && want.every((k, n) => k === got[n])) return true;
    }
  return false;
}

export type FormPattern = 'common' | 'squares' | 'perfect';

/** Une écriture factorisée : un produit dont un facteur est une somme, ou (a + b)ⁿ. */
export function isProductForm(t: Term): boolean {
  if (t.k === 'pow') return t.a.k === 'add';
  if (t.k !== 'mul') return false;
  return flatMul(t).some((f) => f.k === 'add' || (f.k === 'pow' && f.a.k === 'add'));
}

/** Ce qui reste à faire DANS les facteurs d'un produit (parenthèses, produits). */
function innerDebt(t: Term): number {
  const fs = t.k === 'pow' ? [t] : flatMul(t);
  return fs.reduce((s, f) => {
    const g = f.k === 'pow' && f.a.k === 'add' ? f.a : f;
    return s + (g.k === 'add' ? unexpanded(g) + nestedSums(g) : unexpanded(g));
  }, 0);
}

const gcdBig = (a: bigint, b: bigint): bigint => (b === 0n ? (a < 0n ? -a : a) : gcdBig(b, a % b));
/** symboles d'un terme (« 3x²y » → x, y) */
function symsOf(t: Term, out = new Set<string>()): Set<string> {
  if (t.k === 'sym') out.add(t.n);
  else if (t.k === 'add' || t.k === 'mul') t.c.forEach((c) => symsOf(c, out));
  else if (t.k === 'pow' || t.k === 'div' || t.k === 'binom') {
    symsOf(t.a, out);
    symsOf(t.b, out);
  } else if (t.k === 'fn') symsOf(t.a, out);
  return out;
}
/**
 * Une somme qui ne se factorise plus par un facteur commun : ses coefficients entiers
 * sont premiers entre eux, aucune lettre n'est dans tous ses termes.
 */
function primitiveSum(f: Term): boolean {
  const c = canon(f);
  if (c.k !== 'add') return true;
  let g = 0n;
  let integral = true;
  let common: Set<string> | undefined;
  for (const t of c.c) {
    const fs = t.k === 'mul' ? t.c : [t];
    const num = fs.find((u) => u.k === 'num');
    const coef = num && num.k === 'num' ? num.v : { n: 1n, d: 1n };
    if (coef.d !== 1n) integral = false;
    g = gcdBig(g, coef.n);
    const syms = new Set<string>();
    fs.filter((u) => u.k !== 'num').forEach((u) => symsOf(u, syms));
    const prev: Set<string> | undefined = common;
    common = prev ? new Set([...prev].filter((x) => syms.has(x))) : syms;
  }
  if (integral && g > 1n) return false;
  if (common && common.size > 0) return false;
  return !hasRationalRoot(c);
}
/**
 * moteur v41 : une somme en UNE lettre de degré 2 ou plus qui a une racine rationnelle se
 * factorise encore (« x² − 5x + 6 » = (x − 2)(x − 3), « X² − 1 ») ; « x² + 1 », « x² − 2 »,
 * « X² + X + 1 » non.
 */
function hasRationalRoot(f: Term): boolean {
  let rf;
  try {
    rf = toRatFn(f);
  } catch {
    return false;
  }
  if (!polyIsConstant(rf.d)) return false;
  const syms = [...polySymbols(rf.n)];
  if (syms.length !== 1) return false;
  const coefs = univariateCoefs(rf.n, syms[0]!);
  if (!coefs || coefs.length < 3) return false;
  const roots = rationalRoots(rf.n, syms[0]!);
  return !!roots && roots.length > 0;
}
/**
 * moteur v41 : factorisations encore possibles dans les facteurs d'un produit — un facteur
 * de degré d qui a une racine rationnelle compte d − 1 (« (X − 1)(X³ − X² − X + 1) » 2,
 * « (X − 1)(X − 1)(X² − 1) » 1), un facteur commun encore à sortir compte 1.
 */
function factorSplits(r: Term): number {
  const fs = r.k === 'pow' ? [r] : flatMul(r);
  return fs.reduce((n, f) => {
    const g = f.k === 'pow' && f.a.k === 'add' ? f.a : f;
    if (g.k !== 'add' || primitiveSum(g)) return n;
    return n + Math.max(1, sumDegree(g) - 1);
  }, 0);
}
/** degré d'une somme en une lettre (sinon 1) */
function sumDegree(f: Term): number {
  try {
    const rf = toRatFn(f);
    const syms = [...polySymbols(rf.n)];
    if (syms.length !== 1 || !polyIsConstant(rf.d)) return 1;
    const c = univariateCoefs(rf.n, syms[0]!);
    return c ? c.length - 1 : 1;
  } catch {
    return 1;
  }
}

/* ------------------------- une seule fraction (moteur v46) ------------------------- */
//
// « D = (n + 1) ÷ (n + 2) − n ÷ (n + 1) » à écrire en UNE fraction irréductible
// (« 1 ÷ ((n + 1)(n + 2)) ») : uₙ₊₁ − uₙ, f(x) − g(x), réduire au même dénominateur.
// La distance compte les gestes du cours :
//   10 + 2(k − 1) + 1  une somme de termes à k dénominateurs différents (k ≥ 1) ;
//   (après « même dénominateur ») une seule fraction N ÷ D :
//     produits encore à développer dans N (+ parenthèses inutiles) ;
//     + 1 si la fraction se simplifie encore (facteur commun à N et D) ;
//   0  la fraction est écrite, N développé, irréductible (les calculs se comptent à part).
// Le dénominateur peut rester factorisé : « (n + 1)(n + 2) » ou « n² + 3n + 2 ».

/** le dénominateur ÉCRIT d'un terme d'une somme (« −n ÷ (n + 1) » → n + 1), sinon null */
function writtenDen(t: Term): Term | null {
  if (t.k === 'div') return t.b;
  if (t.k !== 'mul') return null;
  const fs = flatMul(t);
  const divs = fs.filter((f) => f.k === 'div');
  if (divs.length !== 1 || fs.some((f) => f.k !== 'div' && symsOf(f).size > 0)) return null;
  return (divs[0] as Extract<Term, { k: 'div' }>).b;
}
/** la fraction seule d'une écriture « N ÷ D », « −N ÷ D », « 2 × (N ÷ D) », sinon null */
function singleFraction(r: Term): { n: Term; d: Term } | null {
  if (r.k === 'div') return { n: r.a, d: r.b };
  if (r.k !== 'mul') return null;
  const fs = flatMul(r);
  const divs = fs.filter((f) => f.k === 'div') as Extract<Term, { k: 'div' }>[];
  if (divs.length !== 1 || fs.some((f) => f.k !== 'div' && f.k !== 'num')) return null;
  return { n: divs[0]!.a, d: divs[0]!.b };
}
/** pgcd de deux polynômes en une lettre (coefficients rationnels, degré croissant) */
function uniGcdDegree(a: Q.Rat[], b: Q.Rat[]): number {
  const tidy = (c: Q.Rat[]): Q.Rat[] => {
    const out = [...c];
    while (out.length > 1 && Q.isZero(out[out.length - 1]!)) out.pop();
    return out;
  };
  const zero = (c: Q.Rat[]): boolean => c.length === 1 && Q.isZero(c[0]!);
  /** reste de la division de x par y (y non nul) */
  const rem = (x: Q.Rat[], y: Q.Rat[]): Q.Rat[] => {
    let r = tidy(x);
    const dy = y.length - 1;
    const ly = y[dy]!;
    for (let guard = 0; guard < 64 && !zero(r) && r.length - 1 >= dy; guard++) {
      const k = Q.div(r[r.length - 1]!, ly)!;
      const shift = r.length - 1 - dy;
      for (let i = 0; i <= dy; i++) r[shift + i] = Q.sub(r[shift + i]!, Q.mul(k, y[i]!));
      r = r.slice(0, r.length - 1);
      r = r.length ? tidy(r) : [Q.ZERO];
    }
    return r;
  };
  let x = tidy(a);
  let y = tidy(b);
  for (let guard = 0; guard < 64 && !zero(y); guard++) {
    const r = rem(x, y);
    x = y;
    y = r;
  }
  return x.length - 1;
}
/** pgcd des coefficients entiers d'un polynôme (0 si un coefficient n'est pas entier) */
function intContent(p: ReturnType<typeof toRatFn>['n']): bigint {
  let g = 0n;
  for (const { c } of p.t.values()) {
    if (c.d !== 1n) return 0n;
    g = gcdBig(g, c.n);
  }
  return g;
}
/** la fraction N ÷ D se simplifie-t-elle encore ? (facteur commun, ou nombre commun) */
export function fractionReducible(n: Term, d: Term): boolean {
  let fn;
  let fd;
  try {
    fn = toRatFn(n);
    fd = toRatFn(d);
  } catch {
    return false;
  }
  if (!polyIsConstant(fn.d) || !polyIsConstant(fd.d)) return true;
  const cn = intContent(fn.n);
  const cd = intContent(fd.n);
  if (cn > 1n && cd > 1n && gcdBig(cn, cd) > 1n) return true;
  if (polyIsConstant(fd.n)) return false;
  // un facteur ÉCRIT du dénominateur (ou du numérateur) qui divise l'autre : « (a − b) ÷ (ab(b − a)) »
  const divides = (fs: Term[], other: ReturnType<typeof toRatFn>['n']): boolean =>
    fs.some((f) => {
      const g = f.k === 'pow' ? f.a : f;
      if (symsOf(g).size === 0) return false;
      try {
        const rg = toRatFn(g);
        return polyIsConstant(rg.d) && !polyIsConstant(rg.n) && !!polyDivExact(other, rg.n);
      } catch {
        return false;
      }
    });
  if (divides(flatMul(d), fn.n) || divides(flatMul(n), fd.n)) return true;
  const syms = [...new Set([...polySymbols(fn.n), ...polySymbols(fd.n)])];
  if (syms.length !== 1) return false;
  const a = univariateCoefs(fn.n, syms[0]!);
  const b = univariateCoefs(fd.n, syms[0]!);
  if (!a || !b) return false;
  if (a.every((c) => Q.isZero(c))) return true;
  return uniGcdDegree(a, b) > 0;
}

/**
 * Une somme de fractions à dénominateurs tous différents (« a ÷ b − c ÷ d ») : le nombre de
 * termes du numérateur commun AVANT toute réduction (a × d développé, puis c × b développé) ;
 * null si la somme n'a pas cette forme. Un numérateur écrit avec MOINS de termes a été
 * réduit en même temps (« 1 ÷ ((n + 1)(n + 2)) » d'un coup) : trop d'un coup.
 */
export function combinedNumeratorTerms(r: Term): number | null {
  const terms = r.k === 'add' ? r.c : [r];
  if (terms.length < 2) return null;
  const parts: { n: Term; d: Term | null }[] = terms.map((t) => {
    const one = singleFraction(t);
    return one ? { n: t.k === 'div' ? one.n : withoutDiv(t, one.n), d: one.d } : { n: t, d: null };
  });
  const keys = new Set(parts.map((p) => (p.d ? termKey(canon(p.d)) : '1')));
  if (keys.size !== parts.length) return null;
  let total = 0;
  try {
    for (let i = 0; i < parts.length; i++) {
      const others = parts.filter((_, j) => j !== i && parts[j]!.d).map((p) => p.d!);
      const prod: Term = others.length ? { k: 'mul', c: [parts[i]!.n, ...others] } : parts[i]!.n;
      const rf = toRatFn(prod);
      if (!polyIsConstant(rf.d)) return null;
      total += Math.max(1, rf.n.t.size);
    }
  } catch {
    return null;
  }
  return total;
}
/** « −2 × (N ÷ D) » → « −2 × N » (le numérateur d'un terme signé) */
function withoutDiv(t: Term, n: Term): Term {
  if (t.k !== 'mul') return n;
  const rest = flatMul(t).filter((f) => f.k !== 'div');
  return rest.length ? { k: 'mul', c: [...rest, n] } : n;
}
/** termes écrits du numérateur d'une seule fraction */
export function numeratorTermCount(r: Term): number {
  const one = singleFraction(r);
  const n = one ? one.n : r;
  return n.k === 'add' ? n.c.length : 1;
}

export interface FractionShape {
  h: number;
  /** dénominateurs différents (somme) ; 1 pour une seule fraction */
  dens: number;
  /** termes de la somme (1 pour une seule fraction) */
  parts: number;
  /** produits encore à développer au numérateur */
  numExpand: number;
  reducible: boolean;
  /** la fraction se simplifie ET le facteur commun est écrit en haut et en bas */
  visible: boolean;
  /** une fraction de fractions (« (1 ÷ b − 1 ÷ a) ÷ (b − a) ») : la forme de son numérateur */
  inner?: FractionShape;
}
/** une division écrite dans le terme */
function hasDiv(t: Term): boolean {
  switch (t.k) {
    case 'div':
      return true;
    case 'add':
    case 'mul':
      return t.c.some(hasDiv);
    case 'pow':
    case 'binom':
      return hasDiv(t.a) || hasDiv(t.b);
    case 'fn':
      return hasDiv(t.a);
    default:
      return false;
  }
}
/** un facteur écrit à la fois au numérateur et au dénominateur (« (x − 1)(x + 1) ÷ (x − 1) ») */
function commonFactorWritten(n: Term, d: Term): boolean {
  const keys = (t: Term): Set<string> =>
    new Set(
      flatMul(t)
        .map((f) => (f.k === 'pow' ? f.a : f))
        .filter((f) => symsOf(f).size > 0)
        .map((f) => termKey(canon(f))),
    );
  const kn = keys(n);
  return [...keys(d)].some((k) => kn.has(k));
}
/** Distance à « une seule fraction irréductible, numérateur développé ». */
export function fractionShape(r: Term): FractionShape {
  const one = singleFraction(r);
  if (one && (hasDiv(one.n) || hasDiv(one.d))) {
    // une fraction de fractions : d'abord une seule fraction au numérateur (et au dénominateur),
    // puis diviser (« (a ÷ b) ÷ c = a ÷ (b × c) »)
    const inner = fractionShape(one.n);
    const den = hasDiv(one.d) ? fractionShape(one.d).h + 1 : 0;
    return {
      h: 5 + inner.h + den,
      dens: 1,
      parts: 1,
      numExpand: 0,
      reducible: true,
      visible: false,
      inner,
    };
  }
  if (one) {
    const numExpand = expandCount(one.n) + nestedSums(one.n);
    const reducible = fractionReducible(one.n, one.d);
    // le facteur commun écrit en haut et en bas : il ne reste qu'à simplifier
    const visible = reducible && commonFactorWritten(one.n, one.d);
    const h = !reducible ? numExpand : visible ? 1 : numExpand + 2;
    return { h, dens: 1, parts: 1, numExpand, reducible, visible };
  }
  const terms = r.k === 'add' ? r.c : [r];
  const keys = new Set<string>();
  let fractions = 0;
  for (const t of terms) {
    const d = writtenDen(t);
    if (d) fractions += 1;
    keys.add(d ? termKey(canon(d)) : '1');
  }
  if (!fractions) {
    // un polynôme : la forme développée (la fraction se réduit à un polynôme)
    const numExpand = expandCount(r) + nestedSums(r);
    return {
      h: numExpand,
      dens: 1,
      parts: terms.length,
      numExpand,
      reducible: false,
      visible: false,
    };
  }
  return {
    h: 10 + 2 * (keys.size - 1) + 1,
    dens: keys.size,
    parts: terms.length,
    numExpand: 0,
    reducible: false,
    visible: false,
  };
}

export interface FormShape {
  /** distance à la forme attendue (avant les calculs) */
  h: number;
  /** calculs en plus (règles des puissances) */
  pow: number;
  /** parenthèses inutiles dans une somme */
  nested: number;
  /** écriture factorisée */
  product: boolean;
  /** somme qui montre un facteur commun, a² − b², a² ± 2ab + b² */
  pattern: FormPattern | null;
  /** la forme attendue est atteinte (sans compter les calculs de la ligne) */
  done: boolean;
  /** moteur v46 : une seule fraction (`form: 'fraction'`) */
  frac?: FractionShape;
}

/** Forme du membre de droite d'une ligne « A = … ». */
export function formShape(r: Term, form: KernelForm, answerKey?: string): FormShape {
  const nested = nestedSums(r);
  if (form === 'fraction') {
    const frac = fractionShape(r);
    return {
      h: frac.h,
      pow: powDebt(r),
      nested,
      product: false,
      pattern: null,
      done: frac.h === 0,
      frac,
    };
  }
  if (form === 'canonical') {
    const h = canonicalDistance(r);
    return { h, pow: 0, nested, product: false, pattern: null, done: h === 0 };
  }
  if (form === 'expanded') {
    const ex = expandCount(r);
    const pow = powDebt(r);
    return {
      h: ex + nested,
      pow,
      nested,
      product: false,
      pattern: null,
      done: ex === 0 && nested === 0 && pow === 0,
    };
  }
  if (isProductForm(r)) {
    const inner = innerDebt(r);
    // un facteur qui se factorise encore (« 7(x² − 2x) ») n'est pas la forme attendue,
    // sauf si c'est l'écriture de la réponse de l'énoncé
    const answer = !!answerKey && termKey(canon(r)) === answerKey;
    const splits = answer ? 0 : factorSplits(r);
    const full = splits === 0;
    return {
      // moteur v41 : un facteur qui se factorise encore est plus loin (3 + ce qui reste à
      // factoriser) qu'un produit complet à réduire (3, kernel-sheet.ts)
      h: inner ? 3 : full ? 0 : 3 + splits,
      pow: 0,
      nested,
      product: true,
      pattern: null,
      done: inner === 0 && full,
    };
  }
  const terms = r.k === 'add' ? r.c : [r];
  const pattern: FormPattern | null =
    terms.length < 2
      ? null
      : showsSquares(terms)
        ? 'squares'
        : showsPerfectSquare(terms)
          ? 'perfect'
          : showsCommonFactor(terms)
            ? 'common'
            : null;
  return { h: pattern ? 7 : 10, pow: 0, nested, product: false, pattern, done: false };
}

/* ------------------------- forme canonique (moteur v43) ------------------------- */
//
// a(x − α)² + β : le carré d'une somme « x + p » (une seule lettre, de coefficient 1),
// éventuellement multiplié par un nombre, plus des nombres. La distance compte les gestes
// du cours, de la forme développée à la forme canonique :
//   4  autre chose (une écriture factorisée, une somme qui n'est pas un trinôme…) ;
//   3  le trinôme développé ax² + bx + c, a ≠ 1 (ou a = −1) : mettre a en facteur ;
//   2  a(x² + bx) + c, ou le trinôme x² + bx + c : compléter le carré ;
//   1  a((x + p)² − p²) + c : développer (distribuer a) ;
//   0  a(x + p)² + nombres : la forme est là (les nombres se calculent ensuite).

/** un terme sans lettre */
const constant = (t: Term): boolean => symsOf(t).size === 0;
/** « x + p » (ou « x » seul) : une seule lettre, de coefficient 1, plus un nombre */
function unitBinomial(t: Term, x: string): boolean {
  if (t.k === 'sym') return t.n === x;
  if (t.k !== 'add' || t.c.length !== 2) return false;
  const [a, b] = t.c;
  return (
    (a!.k === 'sym' && a!.n === x && constant(b!)) || (b!.k === 'sym' && b!.n === x && constant(a!))
  );
}
/** (x + p)², éventuellement multiplié par un nombre (« 2(x + 2)² », « −(x − 1)² ») */
function scaledSquare(t: Term, x: string): boolean {
  const sq = (u: Term): boolean => u.k === 'pow' && intExp(u.b) === 2 && unitBinomial(u.a, x);
  if (sq(t)) return true;
  if (t.k !== 'mul') return false;
  const fs = flatMul(t);
  return fs.filter(sq).length === 1 && fs.every((f) => sq(f) || constant(f));
}
/** un monôme c·x ou c·x² (c nombre) ; son degré, sinon 0 */
function monoDegree(t: Term, x: string): number {
  const fs = flatMul(t);
  let d = 0;
  for (const f of fs) {
    if (constant(f)) continue;
    if (f.k === 'sym' && f.n === x) d += 1;
    else if (f.k === 'pow' && f.a.k === 'sym' && f.a.n === x && intExp(f.b) !== null)
      d += intExp(f.b)!;
    else return 0;
  }
  return d;
}
/** le coefficient de x² d'un trinôme écrit terme à terme vaut-il 1 ? */
function unitLeading(terms: Term[], x: string): boolean {
  const lead = terms.filter((u) => monoDegree(u, x) === 2);
  if (lead.length !== 1) return false;
  return flatMul(lead[0]!).every(
    (f) => !constant(f) || (f.k === 'num' && f.v.n === 1n && f.v.d === 1n),
  );
}
/** le produit d'un nombre et d'une somme : la somme, sinon null */
function scaledSum(t: Term): Term | null {
  if (t.k === 'add') return t;
  if (t.k !== 'mul') return null;
  const fs = flatMul(t);
  const sums = fs.filter((f) => f.k === 'add');
  return sums.length === 1 && fs.every((f) => f.k === 'add' || constant(f)) ? sums[0]! : null;
}

/** Distance à la forme canonique (0 : a(x + p)² plus des nombres). */
export function canonicalDistance(r: Term): number {
  const syms = [...symsOf(r)];
  if (syms.length !== 1) return 4;
  const x = syms[0]!;
  const terms = r.k === 'add' ? r.c : [r];
  const vars = terms.filter((u) => !constant(u));
  if (vars.length === 1 && scaledSquare(vars[0]!, x)) return 0;
  if (vars.length === 1) {
    const inner = scaledSum(vars[0]!);
    if (inner) {
      const it = inner.k === 'add' ? inner.c : [inner];
      const iv = it.filter((u) => !constant(u));
      // a((x + p)² − p²) + c
      if (iv.length === 1 && scaledSquare(iv[0]!, x)) return 1;
      // a(x² + bx) + c
      if (
        iv.length === it.length &&
        iv.every((u) => monoDegree(u, x) > 0) &&
        iv.some((u) => monoDegree(u, x) === 2) &&
        iv.every((u) => monoDegree(u, x) <= 2)
      )
        return 2;
    }
  }
  // le trinôme développé, terme à terme
  if (
    vars.length >= 1 &&
    vars.every((u) => monoDegree(u, x) > 0 && monoDegree(u, x) <= 2) &&
    vars.some((u) => monoDegree(u, x) === 2)
  )
    return unitLeading(vars, x) ? 2 : 3;
  return 4;
}

/** Le pas « factoriser » nommé par l'écriture obtenue. */
export function formFactorWhy(from: FormPattern | null, to: Term): string {
  const top = to.k === 'mul' ? flatMul(to) : [to];
  if (top.some((f) => f.k === 'pow' && f.a.k === 'add') || from === 'perfect')
    return 'On reconnaît une identité remarquable : a² + 2ab + b² = (a + b)², a² − 2ab + b² = (a − b)².';
  if (from === 'squares')
    return 'On reconnaît une identité remarquable : a² − b² = (a − b)(a + b).';
  return 'On met le facteur commun en évidence : ka + kb = k(a + b).';
}

/* ------------------------------ moteur v41 ------------------------------ */

type PowerModel = {
  traps?: { line?: string }[];
  choices?: { line?: string }[];
  kernel?: unknown;
};

/** une puissance d'un produit dont un facteur se calcule encore (« (2x)³ », « (−3a²)³ ») */
function powOfProductToCompute(t: Term): boolean {
  switch (t.k) {
    case 'pow': {
      const fs = t.a.k === 'mul' ? flatMul(t.a) : [];
      if (
        fs.length > 1 &&
        fs.some((f) => !isNum(f)) &&
        fs.some(
          (f) =>
            (f.k === 'num' && !(f.v.d === 1n && (f.v.n === 1n || f.v.n === -1n))) || f.k === 'pow',
        )
      )
        return true;
      return powOfProductToCompute(t.a) || powOfProductToCompute(t.b);
    }
    case 'add':
    case 'mul':
      return t.c.some(powOfProductToCompute);
    case 'div':
    case 'binom':
      return powOfProductToCompute(t.a) || powOfProductToCompute(t.b);
    case 'fn':
      return powOfProductToCompute(t.a);
    default:
      return false;
  }
}

/** un produit qui écrit une puissance d'un nombre : « 2³ × x³ » (après « (2x)³ ») */
function numPowProduct(t: Term): boolean {
  if (t.k === 'mul') {
    const fs = flatMul(t);
    if (
      fs.some((f) => f.k === 'pow' && f.a.k === 'num') &&
      fs.some((f) => !isNum(f) && !(f.k === 'pow' && f.a.k === 'num'))
    )
      return true;
    return t.c.some(numPowProduct);
  }
  if (t.k === 'add') return t.c.some(numPowProduct);
  return false;
}

/** un exposant écrit après une lettre ou une parenthèse : « x³ », « (2x)³ » (lexique v41) */
function writesLetterPower(text: string): boolean {
  try {
    return lex(text).some(isSupPow);
  } catch {
    return false;
  }
}

type PowerKernel = {
  plugin?: string;
  form?: KernelForm;
  method?: string;
  goal?: { target?: string };
  start?: string;
  answer?: string;
  path?: string[];
  givens?: string[];
  claims?: { rels?: string[]; at?: Record<string, string> }[];
};

/**
 * La feuille se sert-elle de ce que sait le moteur v41 ? Une app v40 la lirait ou la
 * jugerait autrement :
 *  - un exposant écrit après une lettre ou une parenthèse (« x³ ») ;
 *  - un cube face à une constante, méthode `factor` (kernel-poly.ts, `cube`) ;
 *  - développer : un pas du corrigé qui n'avance qu'avec le compte des produits
 *    (« (x − 1)(x + 1)(x − 2) » → « (x² − 1)(x − 2) ») ou des puissances
 *    (« (2x)² » → « 2² × x² ») ;
 *  - factoriser : une ligne du corrigé dont un facteur a une racine (« (x − 1)(x² − 1) »).
 * `cubeForm` : la forme « cube » d'une ligne (kernel-poly.ts, injecté : pas de cycle).
 */
export function usesKernelPowers(
  model: PowerModel,
  cubeForm?: (line: string, x: string) => boolean,
): boolean {
  const k = model.kernel as PowerKernel | undefined;
  if (!k || !['algebra', 'formula', 'claims'].includes(k.plugin ?? '')) return false;
  const lines = [k.start ?? '', ...(k.path ?? []), ...(k.givens ?? []), k.answer ?? ''];
  const texts = [
    ...lines,
    ...(model.traps ?? []).map((t) => t.line ?? ''),
    ...(model.choices ?? []).map((c) => c.line ?? ''),
    ...(k.claims ?? []).flatMap((c) => [...(c.rels ?? []), ...Object.values(c.at ?? {})]),
  ];
  if (texts.some(writesLetterPower)) return true;
  const x = k.goal?.target ?? 'x';
  if (k.method === 'factor' && cubeForm && texts.some((t) => !!t && cubeForm(t, x))) return true;
  if (!k.form) return false;
  const valueOf = (t: string): Term | null => {
    const p = parseStatement(t);
    if (!p.ok || p.s.k !== 'rel' || p.s.rel !== '=') return null;
    return p.s.l.k === 'sym' && p.s.l.n === x ? p.s.r : null;
  };
  const steps = [k.start ?? '', ...(k.path ?? [])].map(valueOf);
  const answer = valueOf(k.answer ?? '');
  const answerKey = answer ? termKey(canon(answer)) : '';
  for (let i = 1; i < steps.length; i++) {
    const a = steps[i - 1];
    const b = steps[i];
    if (!a || !b) continue;
    if (k.form === 'expanded') {
      if (unexpanded(a) === unexpanded(b) && expandCount(b) < expandCount(a)) return true;
      if (powOfProductToCompute(a) && numPowProduct(b)) return true;
    } else if (isProductForm(b) && termKey(canon(b)) !== answerKey) {
      const fs = b.k === 'pow' ? [b] : flatMul(b);
      if (
        fs.some(
          (f) =>
            (f.k === 'pow' && f.a.k === 'add' ? f.a : f).k === 'add' &&
            hasRationalRoot(f.k === 'pow' && f.a.k === 'add' ? f.a : f),
        )
      )
        return true;
    }
  }
  return false;
}
